/*
 * TunePad
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */
import PythonWorker from 'web-worker:./worker.js';

/**
 * TypeScript facing interface for Pyodide. The actual pyodide runtime is 
 * loaded in a web worker (/assets/js/PythonWorker.js)
 */
export class PythonRuntime {

    /// Python pyodide version number (e.g. 3.11.2)
    public static VERSION = "3.11.2";

    /// singleton pattern
    private static instance : (PythonRuntime | null) = null;

    /// worker thread interfaces with Pyodide
    private readonly worker: Worker;

    /// waiting list for python compile requests
    private readonly queue = new Array<CompileRequest>();

    /// keeps track of previously compiled results
    private readonly cache = new Map<string, CompileResponse>();

    /// list of all python cells in a playbook known to the runtime
    private readonly modules = new Map<string, PythonCell>();

    /// list of current requests being processed
    private active: CompileRequest[] = [];

    /// current list of dependency warnings
    private warnings = new Array<PythonError>();

    /// list of callbacks listening for warnings
    private listeners = new Map<string, WarningListener>();

    /// used to send keyboard interrupt signals to Pyodide in case of an infinite loop
    //private interruptBuffer = new Uint8Array(new SharedArrayBuffer(1));
    // FIXME: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/SharedArrayBuffer#security_requirements
    // SharedArrayBuffer constructor is unavailable due to security reasons, so this 
    // doesn't currently work.
    private interruptBuffer = new Uint8Array(1);


    /**
     * Start the single python runtime. This is safe to call multiple times.
     * @param workerPath Path to the PythonWorker.js file (e.g. '/assets/js/PythonWorker.js')
     * @param pythonPath Path to python source files (e.g. '/assets/python')
     */
    public static init(
        workerPath : string = '/assets/js/PythonWorker.js',
        pythonPath : string = '/assets/python') : PythonRuntime 
    {
        if (PythonRuntime.instance === null) {
            PythonRuntime.instance = new PythonRuntime(workerPath, pythonPath);
        }
        return PythonRuntime.instance;
    }


    /// private constructor for singleton pattern
    private constructor(workerPath : string, pythonPath : string) {

        // create and initialize web worker
        this.worker = new PythonWorker(); //Worker(workerPath);
        this.worker.postMessage({ action: "init", pythonPath : pythonPath });

        // set up the interrupt buffer
        this.worker.postMessage({ action: "setInterruptBuffer", buffer: this.interruptBuffer });

        // service any pending runtime requests in our queue
        setInterval(() => { PythonRuntime.instance?.processQueue(); }, 20);

        // convert worker responses into CompileResponse objects and send to callbacks
        this.worker.onmessage = (event) => {
            const data = event.data;
            if (!data) return;


            // Python asks to set attributes of DOM elements
            if (data.action === 'set-element-attribute') {
                this.setDOMElementAttribute(data.selector, data.attributeName, data.attributeValue);
                return;
            }

            // Python asks for user input
            if (data.action === 'async-user-prompt') {
                const resp = prompt(data.message);
                this.worker.postMessage({ action : 'async-user-prompt', response : resp });
                return;
            }

            const request = this.findActiveRequest(data.id ?? -1);

            // the 'init' message responds with a python version number
            if (data.python) {
                PythonRuntime.VERSION = data.python;
            }

            // preload response gets processed with no extra information
            else if (request?.action === 'preload') {
                request.callback(new CompileResponse(request));
            }

            else if (request) {

                // have any newer requests with updated code been added?
                // if so, throw away this request
                for (let r of this.queue) {
                    if (r.uuid === request.uuid && r.action === request.action && r.code !== request.code) {
                        return;
                    }
                }

                let response = new CompileResponse(request);
                response.output = data.output;
                response.globals = data.globals;
                response.errors = data.errors;
                response.dependencies = data.dependencies;
                response.result = data.result ?? undefined;

                // clear warnings for this cell
                this.warnings = this.warnings
                    .filter((w) => (w.target !== response.uuid) && (w.source !== response.uuid));

                // cell updated dependency warnings
                for (const [key, resp] of this.cache) {
                    if (key !== response.uuid && resp.dependencies.includes(response.name)) {
                        this.warnings.push({
                            name: "Dependency Warning",
                            message: `Cell '${response.name}' has been updated.`,
                            details: "", line: -1,
                            target: resp.uuid,
                            source: response.uuid
                        });
                    }
                }

                // provide tunepad trace object
                if (response.globals.has('__tunepad_trace')) {
                    response.trace = response.globals.get('__tunepad_trace').value;
                }

                // cache result of compile
                if (response.hasNoErrors) {
                    this.cache.set(response.uuid, response);
                }

                // callback
                request.callback(response);

                // send out warnings
                this.listeners.forEach((callback, uuid) => {
                    callback(this.warnings.filter((w) => w.target === uuid));
                });
            }
        }
    }


    private findActiveRequest(id : number) : CompileRequest | undefined {
        for (let i=0; i<this.active.length; i++) {
            if (this.active[i].id === id) {
                const result = this.active[i];
                this.active.splice(i, 1);
                return result;
            }
        }
        return undefined;
    }


    private sendRenameWarnings(cell : PythonCell, oldName : string) {
        // cell renamed dependency warnings
        for (let [key, resp] of this.cache) {
            if (key === cell.uuid) continue;
            if (resp.dependencies.includes(oldName)) {

                const warn = {
                    name: "Dependency Warning",
                    message: `Cell '${oldName}' was renamed to '${cell.name}'.`,
                    details: "", line: -1,
                    target: resp.uuid,
                    source: cell.uuid
                };
                this.warnings.push(warn);
                const callback = this.listeners.get(resp.uuid);
                if (callback) callback([ warn ]);
            }
        }
    }


    /// should be called when parent component unmounts
    public static destroy() {
        if (PythonRuntime.instance) {
            const rt = PythonRuntime.instance;
            rt.worker.terminate();
            rt.active = [];
            rt.cache.clear();
            rt.modules.clear();
        }
    }


    /**
     * Schedule a python compile. If force is true, force a recompile even if the code hasn't changed
     */
    public static compile(cell: PythonCell, force: boolean = false): Promise<CompileResponse> {
        return new Promise((callback: PythonCallback) => {
            PythonRuntime.instance?.queue.push(new CompileRequest(cell, "compile", callback, force));
        });
    }

    /** 
     * Rename a cell (just calls compile)
     */ 
    public static async renameCell(cell: PythonCell, oldName : string): Promise<CompileResponse> {
        const result = await PythonRuntime.compile(cell, true);
        PythonRuntime.instance?.sendRenameWarnings(cell, oldName);
        return result;
    }


    /**
     * Run the given python code in the context of a cell
     */
    public static runCode(code : string, cell: PythonCell): Promise<CompileResponse> {
        return new Promise((callback: PythonCallback) => {
            PythonRuntime.instance?.sendRequest(new CompileRequest(cell, "run-code", callback, true, code));
        });
    }


    /**
     * Preload cell as module in the virtual pyodide filesystem
     */
    public static preload(cell : PythonCell): Promise<CompileResponse> {
        return new Promise((callback: PythonCallback) => {
            PythonRuntime.instance?.queue.push(new CompileRequest(cell, "preload", callback));
        });
    }


    /** 
     * Preload cells as modules in the virutal pyodide filesystem
     */
    public static preloadAll(cells: Array<PythonCell>): Promise<boolean> {
        const rt = PythonRuntime.instance;
        return new Promise(async (success) => {
            for (const cell of cells) {
                rt?.queue.push(new CompileRequest(cell, "preload", (response: CompileResponse) => {
                    console.log('preload response for ' + response.name);
                }));
            }
            success(true);
        });
    }


    /**
     * Get warning callbacks when cell dependencies are updated
     */
    public static addWarningListener(uuid : string, listener : WarningListener) {
        PythonRuntime.instance?.listeners.set(uuid, listener);
    }

    public static removeWarningListener(uuid : string) {
        PythonRuntime.instance?.listeners.delete(uuid);
    }


    /// Send offscreen canvas to webworker
    public static registerCanvas(name : string, canvas : HTMLCanvasElement) {
        if (canvas != null) {
            const offscreen = canvas.transferControlToOffscreen();
            const python = PythonRuntime.instance;
            python?.worker.postMessage({ action: "register-canvas", name : name, canvas: offscreen }, [offscreen]);
        }
    }


    /// disconnect offscreen canvas
    public static unregisterCanvas(name : string) {
        const python = PythonRuntime.instance;
        python?.worker.postMessage({ action: 'unregister-canvas', name : name });
    }


    /// delete a cell
    public static deleteCell(cell: PythonCell) {
        const rt = PythonRuntime.instance;
        if (rt) {
            rt.modules.delete(cell.uuid);
            rt.cache.delete(cell.uuid);
            rt.worker.postMessage({
                id: CompileRequest.REQUEST_ID++,
                uuid: cell.uuid,
                name: cell.name,
                code: '',
                action: "delete"
            });

            // cell deleted dependency warnings
            for (const [key, resp] of rt.cache) {
                if (key !== cell.uuid) {
                    if (resp.dependencies.includes(cell.name)) {
                        rt.warnings.push({
                            name: "Dependency Warning",
                            message: `Cell '${cell.name}' was deleted.`,
                            details: "", line: -1,
                            target: key,
                            source: cell.uuid
                        });
                    }
                }
            }
        }
    }


    public static getWarnings() {
        return PythonRuntime.instance!.warnings;
    }

    private hasWarnings(uuid : string) : boolean {
        return this.warnings.find(warn => warn.target === uuid) !== undefined;
    }

    /**
     * Create a directory in Pyodide's internal file system
     * @param path directory name
     */
    public static mkdir(path : string) {
        const action = 'mkdir';
        PythonRuntime.instance!.worker.postMessage({ action, path });
    }

    /**
     * Read text data from a file in Pyodide's internal file system
     */
    public static async readFile(path : string) : Promise<string> {
        const cell = { uuid: 'global_uuid', name: path, code: '', canvas: '' };
        const code = `from pathlib import Path\nPath("${path}").read_text()`;
        const resp = await PythonRuntime.runCode(code, cell);
        return resp.result;
    }


    /**
     * Write lines of text data file to Pyodide's internal file system
     * @param file full path and filename to write
     * @param data text data to write to the file
     */
    public static writeFile(file : string, data : string) {
        const action = 'write-file';
        PythonRuntime.instance!.worker.postMessage({ action, file, data });
    }

    /**
     * Delete a file from Pyodide's internal file system
     * @param file full path and filename to delete
     */
    public static deleteFile(file : string) {
        const action = 'delete-file';
        PythonRuntime.instance!.worker.postMessage({ action, file });
    }

    /**
     * Rename a file in Pyodide's internal file system
     * @param file current file name and path
     * @param target new file name and path
     */
    public static moveFile(file : string, target : string) {
        const action = 'move-file';
        PythonRuntime.instance!.worker.postMessage({ action, file, target });
    }


    private equivalentCode(a: string, b: string) {
        function clip(c: string) {
            return c
                .split('\n')
                .filter((l) => (l !== '' && !l.startsWith('#')))
                .join('\n');
        }
        return clip(a) === clip(b);
    }

    private equivalent(request: CompileRequest, response: CompileResponse) {
        return (
            !request.force &&
            request.name === response.name &&
            request.action === response.action &&
            this.equivalentCode(request.code, response.code)
        );
    }


    /// fired by the timer to process any pending compile requests
    private processQueue() {

        if (this.queue.length > 0) {

            // get the first request in the queue
            let request = this.queue.shift() as CompileRequest;

            // update our list of python modules
            this.modules.set(request.uuid, request.cell);

            // pre-cached?
            let response = this.cache.get(request.uuid);
            if (response && response.code &&
                !this.hasWarnings(request.uuid) &&
                this.equivalent(request, response)) {
                request.callback(response);
            }

            // no cached result, so call the webworker
            else {
                this.sendRequest(request);
            }
        }

        // monitor active compiles in case of infinite loops
        /*
        else if (this.active && this.active.initiatedTime > 0 && this.active.action === 'compile') {
            const elapsed = Date.now() - this.active.initiatedTime;

            // pull the plug after 10 seconds
            if (elapsed > 10000 && elapsed < 11000) {

                //fire interrupt using shared array buffer with webworker
                // to simulate a keyboard interrupt
                // https://pyodide.org/en/stable/usage/keyboard-interrupts.html
                this.interruptBuffer[0] = 2; // 2 stands for SIGINT.
            }
        }
        */
    }


    private sendRequest(request : CompileRequest) {

        this.active.push(request);

        // clear the keyboard interrupt buffer
        this.interruptBuffer[0] = 0;

        // set initiated time so that we can catch infinite loops
        request.initiatedTime = Date.now();

        // delete any previously saved results
        this.removeFromCache(request.uuid);

        // ask the worker to compile our source code
        this.worker.postMessage({
            id: request.id,
            uuid: request.uuid,
            name: request.name,
            action: request.action,
            code: request.code,
            canvas: request.canvas
        });
    }


    private removeFromCache(uuid: string) {
        this.cache.get(uuid)?.close();
        this.cache.delete(uuid);
    }

    // list of python reserved words to make sure cells have valid python identifier names
    private static readonly _python_keywords = [
        'and',
        'as',
        'assert',
        'break',
        'class',
        'continue',
        'def',
        'del',
        'elif',
        'else',
        'except',
        'exec',
        'False',
        'finally',
        'for',
        'from',
        'global',
        'if',
        'import',
        'in',
        'is',
        'lambda',
        'None',
        'nonlocal',
        'not',
        'or',
        'pass',
        'raise',
        'return',
        'True',
        'try',
        'while',
        'with',
        'yield'
    ];
    

    /** 
     * Convert a string into a legal python identifier
     * Append a numerical suffix as many times as needed until unique value is produced.
     */
    public static makePythonSafe(name: string, uuid: string) {
        let result = '';
        for (const char of name) {
            let c = char.charCodeAt(0);
            if ((c >= 48 && c <= 57) ||    // 0-9
                (c >= 97 && c <= 199) ||   // a-z
                (c >= 65 && c <= 90) ||    // A-Z
                (c === 95))                // '_'
            {
                result += char;
            }
            else if (c === 32) { // SPACE
                result += '_';
            }
            else if (c === 45) { // dash
                result += '_';
            }
        }

        if (result === '') {
            result = 'new_cell';
        }

        // python identifiers can't start with a number
        if (result.charCodeAt(0) >= 48 && result.charCodeAt(0) <= 57) {
            result = '_' + result;
        }

        // can't be a python keyword
        if (this._python_keywords.includes(result)) {
            result = '_' + result;
        }

        // make sure name is unique
        let original = result;
        let counter = 2;
        while (PythonRuntime.instance?.duplicateName(result, uuid)) {
            result = original + "_" + counter;
            counter++;
        }
        return result;
    }

    private duplicateName(name: string, uuid: string) {
        for (const [key, cell] of this.modules) {
            if (uuid !== key && cell.name === name) return true;
        }
        return false;
    }


/**
 * This originates from Pyodide. It sets the attribute name and value for all elements matching selector.
 */
    private setDOMElementAttribute(selector : string, attributeName : string, attributeValue : string) {
        document.querySelectorAll(selector).forEach(el => { el.setAttribute(attributeName, attributeValue); });
    }

}


export type PythonCallback = (response: CompileResponse) => void;


/**
 * A PythonCell is one unit of a notebook. It has a name, database uuid, and python code.
 * Cells act like individual python files in a module that can be imported from
 * one cell to another.
 */
export interface PythonCell {
    /// use this as primary key (crypto.randomUUID())
    uuid: string;

    /// unique cell name (must be a python identier)
    name: string;

    /// python code
    code: string;

    /// canvas associated with this cell (if any)
    canvas: string;
}


/**
 * Interface for requesting compiles
 */
class CompileRequest {

    public static REQUEST_ID : number = 0;

    public readonly id;

    constructor(cell: PythonCell, action: string, callback: PythonCallback, force: boolean = false, code?: string) {
        this.id = CompileRequest.REQUEST_ID++;
        this.cell = cell;
        this.action = action;
        this.callback = callback;
        this.code = code ?? this.cell.code;
        this.force = force;
    }

    public readonly cell: PythonCell;

    /// resource id for this request (e.g. gadget id)
    public get uuid(): string { return this.cell.uuid; }

    /// name of the tunepad cell
    public get name(): string { return this.cell.name; }

    /// python source code
    public code: string;

    /// canvas associated with this cell (if any)
    public get canvas(): string { return this.cell.canvas; }

    /// action requested ("compile", "run", "play", "pause", "canvas")
    public readonly action: string;

    /// stores promise callback while waiting for pyodide to run
    public readonly callback: PythonCallback;

    /// force a recompile even if code hasn't changed?
    public force: boolean = false;

    /// when was this request first sent to Pyodide to run?
    initiatedTime : number = -1;
}


export class CompileResponse {

    /// matches request id
    readonly id : number;

    /// immutable resource id for this response
    readonly uuid: string;

    /// python source code that generated this response
    readonly code: string;

    /// name of python cell that generated this response
    readonly name: string;

    /// action requested ("compile", "run", "play", "pause", "rename", "canvas")
    public readonly action: string;

    /// list of python standard out (from print statements)
    public output: string[] = [];

    /// trace object generated by the tunepad python api
    public trace : any;

    /// list of python errors resulting from compile
    public errors: PythonError[] = [];

    /// a list of other cell names that this cell imports
    public dependencies: string[] = [];

    /// Python global functions, variables, objects
    public globals: any;

    /// when we use runCodeInContext, python can return a result
    public result: any;


    constructor(request: CompileRequest) {
        this.id = request.id;
        this.uuid = request.uuid;
        this.code = request.code;
        this.name = request.name;
        this.action = request.action;
    }

    public get hasErrors(): boolean {
        return this.errors.length > 0;
    }

    public get hasNoErrors(): boolean {
        return this.errors.length === 0;
    }

    /// cleanup any resources associated with this response
    public close(): void { }

}


export interface PythonError {

    /// name (python type) of the syntax or runtime error
    name: string,

    /// description of the error
    message: string,

    /// details like stack trace, etc
    details ? : string,

    /// line number of the error (-1 means no line number information available)
    line: number,

    /// target cell ID that this error or warning applies to (-1 means the current cell)
    target ? : string,

    /// cell ID that generated this error or warning
    source ? : string
}

type WarningListener = (warns: PythonError[]) => void;
